<script setup lang="ts">
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {ref, watch} from "vue";
import {healthcenterApi} from "@/container";
import {useSystemStore} from "@/stores/system.store";
import {Healthcenter} from "@/core/interfaces/healthcenter/Healthcenter";
import HealthcenterInformation from "@/healthcenter/components/HealthcenterInformation.vue";
import {useToastStore} from "@/stores/toast.store";
import {storeToRefs} from "pinia";
import {ChevronRight} from "@lucide/vue";
import {useRouter} from "vue-router";
import HealthcenterPhoto from "@/healthcenter/components/HealthcenterPhoto.vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import HealthcenterWarehouses from "@/disruption-logistics/components/home/HealthcenterWarehouses.vue";

const loading = ref(true);
const healthcenter = ref<Healthcenter | null>(null);
const {environment, groupingLogo} = storeToRefs(useSystemStore());
const {showSuccess, showError} = useToastStore();
const router = useRouter();

/**
 * Read the healthcenter information as soon as the environment is available,
 * which can happen after this view is mounted on a full reload.
 */
watch(() => environment.value?.entityId, (entityId) => {
    if (!entityId) {
        return;
    }

    healthcenterApi().read(entityId)
        .then(response => {
            healthcenter.value = response;
        })
        .catch(() => showError({content: "Les informations de la pharmacie n'ont pas pu être chargées."}))
        .finally(() => loading.value = false);
}, {immediate: true});

/**
 * Update healthcenter information.
 *
 * @param formData
 */
const onInformationUpdate = (formData: { name?: string; url?: string; mssante_email?: string | null; contacts?: any[] }) => {
    if (environment.value?.entityId) {
        healthcenterApi().update(environment.value.entityId, formData)
            .then(response => {
                healthcenter.value = response.healthcenter;
                showSuccess({content: "Les informations de la pharmacie ont bien été mises à jour."});
            });
    }
};

/**
 * Update healthcenter image.
 */
const handleImageUpdate = (imageId: number) => {
    if (environment.value?.entityId) {
        healthcenterApi().uploadMedia(environment.value.entityId, imageId)
            .then(() => showSuccess({content: "L'image de votre pharmacie a bien été mise à jour"}))
            .catch(() => showError({content: "L'image de votre pharmacie n'a pas pu être mise à jour"}));
    }
};

</script>

<template>
  <BaseFlexSpinner v-if="loading" />
  <div v-else>
    <div class="gradient px-6 py-4">
      <div class="flex flex-row justify-space-between items-center text-white gap-20">
        <h2 class="text-white">
          Complétez le profil de votre pharmacie pour améliorer votre vitrine sur l'application patient
        </h2>
        <div
          class="flex flex-col items-center bg-white rounded-lg py-2 pl-2 border-2 border-transparent hover:border-primary cursor-pointer"
          @click="router.push({name: 'grouping'})"
        >
          <div class="flex flex-row items-center">
            <img
              v-if="groupingLogo"
              class="h-12 w-auto"
              :src="groupingLogo"
              alt="Apodis"
            >
            <p v-else>
              {{ environment?.entity?.grouping?.name || "Aucun groupement" }}
            </p>
            <ChevronRight class="w-12 text-gray-300" />
          </div>
        </div>
      </div>
    </div>
    <div class="p-4">
      <div class="flex flex-row gap-10">
        <div class="flex flex-1">
          <div class="flex flex-col w-full gap-10">
            <HealthcenterInformation
              v-if="healthcenter"
              :healthcenter="healthcenter"
              @update="onInformationUpdate"
            />
            <BaseCard class="p-4">
              <div class="flex flex-row justify-between items-center">
                <h2>Séléctionnez votre agence grossiste</h2>
                <p
                  class="text-primary text-sm hover:underline cursor-pointer"
                  @click="router.push({name: 'disruption-logistics'})"
                >
                  Voir les fonctionalités Rupture >
                </p>
              </div>
              <p class="text-sm text-gray-500 pt-1 pb-2">
                En séléctionnant votre agence, vous bénéficierez des fonctionnalités Rupture d'Apodis
              </p>
              <HealthcenterWarehouses />
            </BaseCard>
          </div>
        </div>
        <div class="flex flex-col gap-10">
          <HealthcenterPhoto v-if="healthcenter" :healthcenter="healthcenter" @on-image-upload="handleImageUpdate" />
          <div class="flex flex-col items-center gap-4">
            <h2>Découvrez l'app patient</h2>
            <img
              alt="iphone mockup"
              src="@/assets/images/iphone-mockup.png"
              class="w-44 h-auto cursor-pointer"
              @click="router.push({name: 'patient-administration.application'})"
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
